import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './move-files-dialog.html';
import type { Document, DocumentFile } from '../services/api.types';
import { listDocuments, moveFile } from '../services/api.service';

type PickedFile = {
  file: DocumentFile;
  selected: boolean;
};

@customElement({ name: 'move-files-dialog', template })
export class MoveFilesDialog {
  private readonly i18n: I18N = resolve(I18N);

  /** The document the files currently belong to. */
  @bindable public sourceDocumentId!: string;
  @bindable public sourceDocumentTitle = '';
  /** Files eligible to move (existing files of the source document, not marked for removal). */
  @bindable public files: DocumentFile[] = [];
  /** File IDs pre-checked when the dialog opens (e.g. the single file the user clicked "move" on). */
  @bindable public preselectedFileIds: string[] = [];
  /** Called with (movedFileIds, targetDocumentId, targetDocumentTitle, errorMessage?) once the move attempt finishes.
   *  errorMessage is set when one of the moves failed partway through; movedFileIds still lists what succeeded. */
  @bindable public moved!: (movedFileIds: string[], targetDocumentId: string, targetDocumentTitle: string, errorMessage?: string) => void;
  @bindable public closed!: () => void;

  public pickedFiles: PickedFile[] = [];
  public query = '';
  public searching = false;
  public results: Document[] = [];
  public selectedTarget: Document | null = null;
  public moving = false;
  public error = '';

  private _debounce: ReturnType<typeof setTimeout> | null = null;
  private _currentQuery = '';
  private _keyHandler!: (e: KeyboardEvent) => void;

  public binding(): void {
    this.pickedFiles = this.files.map(f => ({
      file: f,
      selected: this.preselectedFileIds.includes(f.id),
    }));
    // Always start from a clean slate — this dialog instance may be reused/rebound
    // between successive move actions, so any leftover search/selection must be cleared.
    this.query = '';
    this.searching = false;
    this.results = [];
    this.selectedTarget = null;
    this.moving = false;
    this.error = '';
    this._currentQuery = '';
    if (this._debounce) {
      clearTimeout(this._debounce);
      this._debounce = null;
    }
  }

  public attached(): void {
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.cancel();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
    if (this._debounce) clearTimeout(this._debounce);
  }

  public get selectedCount(): number {
    return this.pickedFiles.filter(p => p.selected).length;
  }

  public get canMove(): boolean {
    return this.selectedCount > 0 && this.selectedTarget !== null && !this.moving;
  }

  public get introText(): string {
    return this.i18n.tr('documents.moveFiles.intro', { title: this.sourceDocumentTitle });
  }

  public get summaryText(): string {
    if (!this.selectedTarget) return '';
    return this.i18n.tr('documents.moveFiles.summary', {
      count: this.selectedCount,
      source: this.sourceDocumentTitle,
      target: this.selectedTarget.title,
    });
  }

  public get moveButtonText(): string {
    return this.moving
      ? this.i18n.tr('documents.moveFiles.moving')
      : this.i18n.tr('documents.moveFiles.moveButton', { count: this.selectedCount });
  }

  public resultMeta(doc: Document): string {
    return this.i18n.tr('documents.moveFiles.resultMeta', {
      count: doc.files.length,
    });
  }

  public toggleFile(pf: PickedFile): void {
    pf.selected = !pf.selected;
  }

  public selectAll(): void {
    this.pickedFiles.forEach(p => { p.selected = true; });
  }

  public selectNone(): void {
    this.pickedFiles.forEach(p => { p.selected = false; });
  }

  public onQueryInput(): void {
    this.selectedTarget = null;
    if (this._debounce) clearTimeout(this._debounce);
    const q = this.query.trim();
    if (!q) {
      this.results = [];
      this.searching = false;
      return;
    }
    this.searching = true;
    this._debounce = setTimeout(() => { void this._doSearch(q); }, 250);
  }

  private async _doSearch(query: string): Promise<void> {
    this._currentQuery = query;
    try {
      const response = await listDocuments(
        'createdAt', true, 1, 8, undefined, undefined, query, false,
        undefined, false, undefined, undefined, false, true);
      if (query !== this._currentQuery) return;
      this.results = response.documents.filter(d => d.id !== this.sourceDocumentId);
    } catch {
      if (query !== this._currentQuery) return;
      this.results = [];
    } finally {
      if (query === this._currentQuery) this.searching = false;
    }
  }

  public pickTarget(doc: Document): void {
    this.selectedTarget = doc;
    this.query = doc.title;
    this.results = [];
  }

  public clearTarget(): void {
    this.selectedTarget = null;
    this.query = '';
    this.results = [];
  }

  public async confirmMove(): Promise<void> {
    if (!this.canMove || !this.selectedTarget) return;
    this.moving = true;
    this.error = '';
    const target = this.selectedTarget;
    const toMove = this.pickedFiles.filter(p => p.selected).map(p => p.file);
    const movedIds: string[] = [];
    let failureMessage: string | undefined;

    for (const f of toMove) {
      try {
        const moved = await moveFile(this.sourceDocumentId, f.id, target.id);
        if (moved.duplicateSkipped) {
          failureMessage = this.i18n.tr('documents.form.duplicateFileSkippedWarning', {
            fileName: moved.fileName,
            duplicateFileName: moved.duplicateFileName ?? moved.fileName,
            documentTitle: moved.duplicateDocumentTitle ?? target.title,
          });
          break;
        }
        movedIds.push(f.id);
      } catch (err: unknown) {
        failureMessage = err instanceof Error ? err.message : this.i18n.tr('documents.moveFiles.moveFailed');
        break;
      }
    }

    this.moving = false;

    if (movedIds.length === 0 && failureMessage) {
      // Nothing moved yet — keep the dialog open so the user can retry without losing their selection.
      this.error = failureMessage;
      return;
    }

    this.moved(movedIds, target.id, target.title, failureMessage);
    this.closed();
  }

  public cancel(): void {
    if (this.moving) return;
    this.closed();
  }
}
